import { useEffect, useRef, useState } from 'react';

import {
    isPdfAvailabilityDueNow,
    resolvePubmedPdfAvailability,
    selectFreshestPdfAvailability,
    shouldRetryDueUnknownPdfAvailability,
    withPdfAvailabilityRefreshState,
    type PdfAvailabilityFields,
    type PubmedPdfAvailabilityDecision,
} from '../lib/pdfAvailability';
import {
    refreshPaperPdfAvailability,
} from '../lib/pdfAvailabilityApi';
import type { PaperPdfAvailabilityResponse } from '../lib/pdfAvailabilityApiContract';

interface PubmedPaperAvailabilityInput extends PdfAvailabilityFields {
    id: string;
}

const MAX_INTERACTIVE_ATTEMPTS_PER_DECISION = 2;
const CONCURRENT_CLAIM_RETRY_DELAY_MS = 5_000;

export function usePubmedPdfAvailability({
    paper,
    isPubmed,
    accessToken,
    accessIdentity,
}: {
    paper: PubmedPaperAvailabilityInput;
    isPubmed: boolean;
    accessToken?: string | null;
    accessIdentity?: string | null;
}): PubmedPdfAvailabilityDecision {
    const [refreshedByPaper, setRefreshedByPaper] = useState<
        Record<string, PaperPdfAvailabilityResponse>
    >({});
    const [activeRefreshKey, setActiveRefreshKey] = useState<string | null>(null);
    const [retryGeneration, setRetryGeneration] = useState(0);
    const attemptedRefreshKeys = useRef(new Set<string>());
    const attemptCounts = useRef(new Map<string, number>());

    const refreshed = refreshedByPaper[paper.id];
    const effectivePaper = selectFreshestPdfAvailability(paper, refreshed);
    const decision = resolvePubmedPdfAvailability(effectivePaper);
    // The decision timestamp, not the bearer token, identifies a refresh
    // generation. Availability is paper-scoped and contains no user data.
    const refreshKey = accessToken && accessIdentity
        ? `${paper.id}:${accessIdentity}:${effectivePaper.pdf_availability_checked_at || 'never'}`
        : null;

    useEffect(() => {
        if (
            !isPubmed
            || !accessToken
            || decision.status !== 'unknown'
            || !refreshKey
            || !isPdfAvailabilityDueNow(effectivePaper.pdf_availability_next_check_at)
            || attemptedRefreshKeys.current.has(refreshKey)
        ) {
            return;
        }

        attemptedRefreshKeys.current.add(refreshKey);
        attemptCounts.current.set(
            refreshKey,
            (attemptCounts.current.get(refreshKey) || 0) + 1,
        );
        setActiveRefreshKey(refreshKey);
        void refreshPaperPdfAvailability({
            paperId: paper.id,
            accessToken,
        }).then((response) => {
            setRefreshedByPaper((current) => ({
                ...current,
                [response.paper_id]: response,
            }));
        }).catch(() => {
            // Keep the cached, fail-closed decision. A remount or a genuinely
            // newer backend decision may try again; provider errors must not
            // create a tight client retry loop.
        }).finally(() => {
            setActiveRefreshKey((current) => current === refreshKey ? null : current);
        });
    }, [
        accessToken,
        accessIdentity,
        decision.status,
        effectivePaper.pdf_availability_next_check_at,
        isPubmed,
        paper.id,
        refreshKey,
        retryGeneration,
    ]);

    useEffect(() => {
        if (!refreshKey || !refreshed) {
            return;
        }
        const completedAttempts = attemptCounts.current.get(refreshKey) || 0;
        if (!shouldRetryDueUnknownPdfAvailability(
            effectivePaper,
            completedAttempts,
            MAX_INTERACTIVE_ATTEMPTS_PER_DECISION,
        )) {
            return;
        }

        // A due-now unknown response normally means another request or the
        // hourly worker owns the short claim. Re-read once after a short delay;
        // future 6/24/72-hour retries never enter this path.
        const retryTimer = setTimeout(() => {
            attemptedRefreshKeys.current.delete(refreshKey);
            setRetryGeneration((current) => current + 1);
        }, CONCURRENT_CLAIM_RETRY_DELAY_MS);
        return () => clearTimeout(retryTimer);
    }, [
        effectivePaper,
        refreshKey,
        refreshed,
        retryGeneration,
    ]);

    return withPdfAvailabilityRefreshState(
        decision,
        Boolean(refreshKey && activeRefreshKey === refreshKey),
    );
}
